// views/Weather.js
import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Spinner,
  Progress,
} from "reactstrap";
import moment from "moment";
import TodaysBlogWidget from "components/Blog/TodaysBlogWidget.jsx";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const WeatherView = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await fetch(`${API_BASE}/weather`);
        const json = await res.json();
        setWeather(json);
      } catch (err) {
        console.error("Failed to load weather data:", err);
        setWeather(null);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" size="lg" />
        <p className="text-muted mt-3">Loading weather data...</p>
      </div>
    );
  }

  if (!weather || !weather.lastData) {
    return (
      <Card className="shadow">
        <CardBody className="text-center py-5">
          <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3" />
          <h3>Error loading weather data</h3>
          <p className="text-muted">Unable to fetch current weather conditions.</p>
        </CardBody>
      </Card>
    );
  }

  const { lastData } = weather;
  const time = new Date(lastData.date).toLocaleTimeString();
  const lastRainDate = new Date(lastData.lastRain);
  const lastRainAgo = moment(lastRainDate).fromNow();
  const diffHours = (new Date() - lastRainDate) / (1000 * 60 * 60);
  const showEventRain = diffHours <= 48;

  // Determine temperature color based on value
  const getTempColor = (temp) => {
    if (temp < 32) return 'info'; // Freezing - blue
    if (temp < 60) return 'primary'; // Cold - blue
    if (temp < 75) return 'success'; // Mild - green
    if (temp < 85) return 'warning'; // Warm - orange
    return 'danger'; // Hot - red
  };

  const tempColor = getTempColor(lastData.tempf);

  return (
    <div>
      <Card className="shadow mb-4">
        <CardHeader className="border-0">
          <Row className="align-items-center">
            <Col>
              <CardTitle tag="h3" className="mb-0">
                <i className="fas fa-cloud-sun mr-2 text-primary" />
                Weather Conditions
              </CardTitle>
              <p className="text-muted mb-0 mt-1" style={{ fontSize: "0.95rem" }}>
                Last updated {moment(lastData.date).fromNow()} at {time}
              </p>
            </Col>
          </Row>
        </CardHeader>
      </Card>

      {/* Main Weather Stats - 8 Card Grid */}
      <Row className="mb-4">
        {/* Temperature */}
        <Col xs="6" lg="3" className="mb-3">
          <div className="d-flex align-items-center">
            <div className={`icon icon-shape bg-gradient-${tempColor} text-white rounded-circle shadow mr-3`}>
              <i className="fas fa-thermometer-half" />
            </div>
            <div>
              <h5 className="text-uppercase text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                Temperature
              </h5>
              <span className="h2 font-weight-bold mb-0">{lastData.tempf}°F</span>
            </div>
          </div>
        </Col>

        {/* Feels Like */}
        <Col xs="6" lg="3" className="mb-3">
          <div className="d-flex align-items-center">
            <div className={`icon icon-shape bg-gradient-${tempColor} text-white rounded-circle shadow mr-3`} style={{ opacity: 0.8 }}>
              <i className="fas fa-temperature-high" />
            </div>
            <div>
              <h5 className="text-uppercase text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                Feels Like
              </h5>
              <span className="h2 font-weight-bold mb-0">{lastData.feelsLike}°F</span>
            </div>
          </div>
        </Col>

        {/* Humidity */}
        <Col xs="6" lg="3" className="mb-3">
          <div className="d-flex align-items-center flex-grow-1">
            <div className="icon icon-shape bg-gradient-info text-white rounded-circle shadow mr-3">
              <i className="fas fa-tint" />
            </div>
            <div className="flex-grow-1">
              <h5 className="text-uppercase text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                Humidity
              </h5>
              <span className="h2 font-weight-bold mb-0">{lastData.humidity}%</span>
              <Progress value={lastData.humidity} className="mt-2" color="info" style={{ height: '4px' }} />
            </div>
          </div>
        </Col>

        {/* Dew Point */}
        <Col xs="6" lg="3" className="mb-3">
          <div className="d-flex align-items-center">
            <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow mr-3">
              <i className="fas fa-droplet" />
            </div>
            <div>
              <h5 className="text-uppercase text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                Dew Point
              </h5>
              <span className="h2 font-weight-bold mb-0">{lastData.dewPoint}°F</span>
            </div>
          </div>
        </Col>

        {/* Last Rain */}
        <Col xs="6" lg="3" className="mb-3">
          <div className="d-flex align-items-center">
            <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow mr-3">
              <i className="fas fa-cloud-rain" />
            </div>
            <div>
              <h5 className="text-uppercase text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                Last Rain
              </h5>
              <span className="h3 font-weight-bold mb-0">{lastRainAgo}</span>
            </div>
          </div>
        </Col>

        {/* Wind Speed */}
        <Col xs="6" lg="3" className="mb-3">
          <div className="d-flex align-items-center">
            <div className="icon icon-shape bg-gradient-warning text-white rounded-circle shadow mr-3">
              <i className="fas fa-wind" />
            </div>
            <div>
              <h5 className="text-uppercase text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                Wind Speed
              </h5>
              <span className="h2 font-weight-bold mb-0">{lastData.windspeedmph} mph</span>
            </div>
          </div>
        </Col>

        {/* Event Rain (if recent) or Daily Rain */}
        <Col xs="6" lg="3" className="mb-3">
          <div className="d-flex align-items-center">
            <div className="icon icon-shape bg-gradient-info text-white rounded-circle shadow mr-3">
              <i className="fas fa-cloud-showers-heavy" />
            </div>
            <div>
              <h5 className="text-uppercase text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                {showEventRain ? 'Event Rain' : 'Daily Rain'}
              </h5>
              <span className="h2 font-weight-bold mb-0">
                {showEventRain ? lastData.eventrainin : lastData.dailyrainin}"
              </span>
            </div>
          </div>
        </Col>

        {/* Barometer */}
        <Col xs="6" lg="3" className="mb-3">
          <div className="d-flex align-items-center">
            <div className="icon icon-shape bg-gradient-success text-white rounded-circle shadow mr-3">
              <i className="fas fa-tachometer-alt" />
            </div>
            <div>
              <h5 className="text-uppercase text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                Barometer
              </h5>
              <span className="h2 font-weight-bold mb-0">{lastData.baromrelin}"</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Blog Widget */}
      <Row className="mb-4">
        <Col md="12">
          <TodaysBlogWidget />
        </Col>
      </Row>

      {/* Rainfall Summary Card */}
      <Row>
        <Col md="12">
          <Card className="shadow">
            <CardHeader className="border-0">
              <h5 className="mb-0">
                <i className="fas fa-cloud-rain mr-2 text-primary" />
                Rainfall Summary
              </h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="4" className="mb-3 mb-md-0">
                  <div className="border-right pr-3">
                    <small className="text-muted d-block mb-1">LAST RAINFALL</small>
                    <h3 className="mb-1">{lastRainAgo}</h3>
                    <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                      {lastRainDate.toLocaleDateString()} at {lastRainDate.toLocaleTimeString()}
                    </p>
                  </div>
                </Col>
                <Col md="8">
                  <Row>
                    <Col xs="6" sm="3" className="mb-3">
                      <small className="text-muted d-block">Hourly</small>
                      <h4 className="mb-0">{lastData.hourlyrainin}"</h4>
                    </Col>
                    <Col xs="6" sm="3" className="mb-3">
                      <small className="text-muted d-block">Daily</small>
                      <h4 className="mb-0">{lastData.dailyrainin}"</h4>
                    </Col>
                    <Col xs="6" sm="3" className="mb-3">
                      <small className="text-muted d-block">Event</small>
                      <h4 className="mb-0">{lastData.eventrainin}"</h4>
                    </Col>
                    <Col xs="6" sm="3" className="mb-3">
                      <small className="text-muted d-block">Yearly</small>
                      <h4 className="mb-0">{lastData.yearlyrainin}"</h4>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default WeatherView;
